import { formatNumber } from 'utils/number';
// Imported as well as re-exported: `export ... from` forwards the names without
// binding them in this module's scope, and coverageSentence below calls them.
import { acres, count } from '../shared/formatters';
import type {
  CategoriesPhaseResponse,
  OverAllocatedSlot,
  OverAllocationCategory,
} from './types';

// Shared with the acreage drift page, which reads the same figures at the same
// precisions. Re-exported rather than moved outright so the call sites in this
// directory keep importing from one place.
export { acres, count, interest, interestPercent } from '../shared/formatters';

/**
 * Formats a share of the unit at 6 decimals — these run to ~0.0017, so the
 * usual 2-decimal acreage precision would render them all as 0.00.
 *
 * @param {number | null | undefined} value - The share.
 * @return {string} The formatted share, or a placeholder when absent.
 */
export const share = (value: number | null | undefined) =>
  formatNumber(value ?? null, 6, 6, 'decimal', true);

/**
 * How each known mechanism is presented. Shared by the list and the drill-down
 * so a slot is named identically in both, and typed against the category union
 * so adding a mechanism to the backend forces a label here.
 */
export const CATEGORY_LABELS: Record<
  OverAllocationCategory,
  { tag: 'red' | 'magenta'; short: string; long: string }
> = {
  understated_gross: {
    tag: 'red',
    short: 'understated gross',
    long: 'Understated gross acreage',
  },
  duplicate_parcel: {
    tag: 'magenta',
    short: 'duplicate parcel',
    long: 'Duplicate records on one parcel',
  },
};

/**
 * Folds a classification verdict onto the rows already on screen.
 *
 * Merged rather than replaced so the table does not flicker or lose the row a
 * reader has expanded — classification is the slower phase and lands well after
 * the roll-up it annotates.
 *
 * A slot with no verdict keeps its identity untouched. That is not the same as a
 * slot verdicted as matching neither mechanism: one means the classification
 * never reached this row, the other is a finding, and collapsing them would read
 * as a clean bill of health.
 *
 * @param {Array} slots - The rows currently displayed.
 * @param {object} verdicts - The classification, keyed by slot key.
 * @return {Array} The rows with their verdicts folded in.
 */
export const mergeVerdicts = (
  slots: OverAllocatedSlot[],
  verdicts: CategoriesPhaseResponse['verdicts']
): OverAllocatedSlot[] =>
  slots.map((row) => {
    const verdict = verdicts[row.key];
    if (!verdict) return row;

    return {
      ...row,
      categories: verdict.categories,
      understatedGrossRows: verdict.understatedGrossRows,
      overClaimedParcels: verdict.overClaimedParcels,
    };
  });

/**
 * A subtract polygon this many times its recorded acreage is called out as
 * oversized. Mirrors `OverAllocatedSlotFinder::OVERSIZED_GEOMETRY_MULTIPLE`,
 * which the coverage counts on the detail header are computed with.
 */
export const OVERSIZED_GEOMETRY_MULTIPLE = 2;

/**
 * The coverage finding, as a sentence rather than a pile of numbers.
 *
 * This is the one line that explains an over-allocated slot, and stating the
 * figures side by side left the reader to find the argument in them. On well
 * 3501120010 the panel read "5,675.40 acres credited across 142 calculations,
 * in a 39.97-acre unit (142x). 142 rows credited with the whole unit" — where
 * the 142 appearing three times is not a coincidence and is the entire
 * explanation: every record was credited the whole unit, so the allocation
 * handed out 142 copies of the same ground.
 *
 * @param {object} coverage - The slot's coverage summary.
 * @param {number} calculationCount - How many calculations feed the slot.
 * @return {string} The finding.
 */
export const coverageSentence = (
  coverage: {
    unitAcres: number | null;
    creditedAcres: number;
    creditedMultiple: number | null;
    rowsCreditedWholeUnit: number;
    rowsWithOversizedGeometry: number;
  },
  calculationCount: number
): string => {
  const unit = acres(coverage.unitAcres);
  const credited = acres(coverage.creditedAcres);
  const whole = coverage.rowsCreditedWholeUnit;

  // Leads with the cause when there is one to lead with.
  const opening =
    whole === calculationCount && whole > 0
      ? `Every one of the ${count(
          calculationCount
        )} calculations was credited with the whole ${unit}-acre unit`
      : `${count(whole)} of the ${count(
          calculationCount
        )} calculations were credited with the whole ${unit}-acre unit`;

  const multiple =
    coverage.creditedMultiple === null
      ? ''
      : ` — ${formatNumber(
          coverage.creditedMultiple,
          0,
          1,
          'decimal',
          true
        )}x the ground it has`;

  const geometry =
    coverage.rowsWithOversizedGeometry > 0
      ? ` ${count(
          coverage.rowsWithOversizedGeometry
        )} of those records also carry a polygon more than ${OVERSIZED_GEOMETRY_MULTIPLE}x their recorded acreage.`
      : '';

  return `${opening}, so the allocation handed out ${credited} acres of participation on ${unit} acres of ground${multiple}.${geometry}`;
};
